import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { motion } from 'framer-motion';
import { format, parseISO } from 'date-fns';
import { de } from 'date-fns/locale';
import { toast } from 'sonner';
import {
  Calendar as CalendarIcon,
  Plus,
  Trash2,
  Building2,
  Sun,
  AlertTriangle,
  Users,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { TeamVacationCalendar } from '../components/TeamVacationCalendar';

export default function CalendarPage() {
  const { user, api } = useAuth();
  const [rules, setRules] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [shutdownForm, setShutdownForm] = useState({
    name: '',
    start_date: '',
    end_date: '',
  });

  const isAdmin = user?.role === 'admin';

  const fetchRules = useCallback(async () => {
    try {
      const response = await api.get('/calendar/rules');
      setRules(response.data);
    } catch (error) {
      console.error('Failed to fetch calendar rules:', error);
      toast.error('Fehler beim Laden der Kalenderregeln');
    } finally {
      setIsLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchRules();
  }, [fetchRules]);

  const handleCreateShutdown = async (e) => {
    e.preventDefault();
    try {
      await api.post('/admin/calendar/rules', {
        name: shutdownForm.name,
        type: 'shutdown',
        start_date: shutdownForm.start_date,
        end_date: shutdownForm.end_date,
        weekdays_only: true,
      });
      toast.success('Betriebsferien erstellt');
      setDialogOpen(false);
      setShutdownForm({ name: '', start_date: '', end_date: '' });
      fetchRules();
    } catch (error) {
      const message = error.response?.data?.detail || 'Fehler beim Erstellen';
      toast.error(message);
    }
  };

  const handleDeleteRule = async (ruleId, ruleName) => {
    try {
      await api.delete(`/admin/calendar/rules/${ruleId}`);
      toast.success(`"${ruleName}" gelöscht`);
      fetchRules();
    } catch (error) {
      toast.error('Fehler beim Löschen');
    }
  };

  const reducedDays = rules.filter(r => r.type === 'reduced_day');
  const shutdowns = rules.filter(r => r.type === 'shutdown');

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="calendar-page" className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground">
            Betriebskalender
          </h1>
          <p className="text-muted-foreground mt-1">
            Urlaubsplanung, Kurzarbeitstage & Betriebsferien
          </p>
        </div>
        {isAdmin && (
          <Button
            onClick={() => setDialogOpen(true)}
            data-testid="add-shutdown-button"
            className="bg-indigo-500 hover:bg-indigo-600 text-white"
          >
            <Plus className="w-4 h-4 mr-2" />
            Betriebsferien hinzufügen
          </Button>
        )}
      </div>

      <Tabs defaultValue="planning" className="w-full">
        <TabsList className="bg-card/50 border border-border">
          <TabsTrigger value="planning" className="data-[state=active]:bg-indigo-500/20">
            <Users className="w-4 h-4 mr-2" />
            Urlaubsplanung
          </TabsTrigger>
          <TabsTrigger value="rules" className="data-[state=active]:bg-amber-500/20">
            <CalendarIcon className="w-4 h-4 mr-2" />
            Kalenderregeln
          </TabsTrigger>
        </TabsList>

        {/* Urlaubsplanung Tab */}
        <TabsContent value="planning" className="mt-6">
          <TeamVacationCalendar />
        </TabsContent>

        {/* Kalenderregeln Tab */}
        <TabsContent value="rules" className="mt-6 space-y-6">
          {/* Info Box */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="p-4 rounded-lg bg-amber-500/10 border border-amber-500/30"
          >
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-muted-foreground">
                <p className="font-medium text-amber-600 dark:text-amber-400 mb-1">Hinweis zur Urlaubsberechnung</p>
                <ul className="list-disc list-inside space-y-1 text-muted-foreground">
                  <li><strong>Kurzarbeitstage</strong> (z.B. 24.12., 31.12.): Nur halber Urlaubstag wird abgezogen</li>
                  <li><strong>Betriebsferien</strong>: Werden automatisch vom Urlaubskonto abgezogen</li>
                  <li>Berechnung erfolgt in <strong>Stunden</strong> (Teilzeit-kompatibel)</li>
                </ul>
              </div>
            </div>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Reduced Days Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <Card className="bg-card/50 border-border">
                <CardHeader>
                  <CardTitle className="text-foreground flex items-center gap-2">
                    <Sun className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                    Kurzarbeitstage
                  </CardTitle>
                  <CardDescription className="text-muted-foreground">
                    Feste jährliche Tage mit reduzierter Arbeitszeit
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  {reducedDays.length === 0 ? (
                    <p className="text-muted-foreground text-sm">Keine Kurzarbeitstage definiert</p>
                  ) : (
                    reducedDays.map((rule) => (
                      <div
                        key={rule.id}
                        className="flex items-center justify-between p-3 rounded-lg bg-muted/50"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-amber-500/20 flex items-center justify-center">
                            <CalendarIcon className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                          </div>
                          <div>
                            <p className="text-foreground font-medium">{rule.name}</p>
                            <p className="text-sm text-muted-foreground">
                              Jährlich am {rule.month_day?.split('-')[1]}.{rule.month_day?.split('-')[0]}. 
                              <span className="ml-2 text-amber-600 dark:text-amber-400">
                                ({(rule.work_factor * 100)}% Arbeitszeit)
                              </span>
                            </p>
                          </div>
                        </div>
                        {isAdmin && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDeleteRule(rule.id, rule.name)}
                            className="text-muted-foreground hover:text-red-600 dark:text-red-400"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    ))
                  )}
                </CardContent>
              </Card>
            </motion.div>

            {/* Shutdowns Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
            >
              <Card className="bg-card/50 border-border">
                <CardHeader>
                  <CardTitle className="text-foreground flex items-center gap-2">
                    <Building2 className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
                    Betriebsferien
                  </CardTitle>
                  <CardDescription className="text-muted-foreground">
                    Zeiträume mit Betriebsschließung (automatischer Urlaubsabzug)
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  {shutdowns.length === 0 ? (
                    <p className="text-muted-foreground text-sm">Keine Betriebsferien definiert</p>
                  ) : (
                    shutdowns.map((rule) => (
                      <div
                        key={rule.id}
                        className="flex items-center justify-between p-3 rounded-lg bg-muted/50"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-indigo-500/20 flex items-center justify-center">
                            <Building2 className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
                          </div>
                          <div>
                            <p className="text-foreground font-medium">{rule.name}</p>
                            <p className="text-sm text-muted-foreground">
                              {format(parseISO(rule.start_date), 'd. MMM', { locale: de })} - {format(parseISO(rule.end_date), 'd. MMM yyyy', { locale: de })}
                            </p>
                          </div>
                        </div>
                        {isAdmin && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDeleteRule(rule.id, rule.name)}
                            data-testid={`delete-shutdown-${rule.id}`}
                            className="text-muted-foreground hover:text-red-600 dark:text-red-400"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    ))
                  )}
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </TabsContent>
      </Tabs>

      {/* Add Shutdown Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-card border-border max-w-md">
          <DialogHeader>
            <DialogTitle className="text-foreground font-heading">
              Betriebsferien hinzufügen
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleCreateShutdown} className="space-y-4">
            <div className="space-y-2">
              <Label className="text-muted-foreground">Bezeichnung</Label>
              <Input
                value={shutdownForm.name}
                onChange={(e) => setShutdownForm({ ...shutdownForm, name: e.target.value })}
                placeholder="z.B. Weihnachtsferien 2026"
                required
                data-testid="shutdown-name-input"
                className="bg-input border-border text-foreground"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-muted-foreground">Von</Label>
                <Input
                  type="date"
                  value={shutdownForm.start_date}
                  onChange={(e) => setShutdownForm({ ...shutdownForm, start_date: e.target.value })}
                  required
                  data-testid="shutdown-start-input"
                  className="bg-input border-border text-foreground"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-muted-foreground">Bis</Label>
                <Input
                  type="date"
                  value={shutdownForm.end_date}
                  onChange={(e) => setShutdownForm({ ...shutdownForm, end_date: e.target.value })}
                  required
                  data-testid="shutdown-end-input"
                  className="bg-input border-border text-foreground"
                />
              </div>
            </div>

            <div className="p-3 rounded-lg bg-indigo-500/10 border border-indigo-500/20">
              <p className="text-sm text-muted-foreground">
                <strong className="text-indigo-600 dark:text-indigo-400">Hinweis:</strong> Betriebsferien werden automatisch
                vom Urlaubskonto aller Mitarbeiter abgezogen. Wochenenden und Feiertage werden nicht berechnet.
              </p>
            </div>

            <DialogFooter className="gap-2">
              <Button
                type="button"
                variant="ghost"
                onClick={() => setDialogOpen(false)}
                className="text-muted-foreground hover:text-foreground"
              >
                Abbrechen
              </Button>
              <Button
                type="submit"
                data-testid="save-shutdown-button"
                className="bg-indigo-500 hover:bg-indigo-600 text-white"
              >
                Speichern
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
